import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { LABELS, type CyclePhase, type DdBasis } from "@match/shared";
import { PHASE_LABEL } from "@/components/session";
import { Button, ErrorNote, Field, SidePanel, inputClass } from "@/components/ui";
import { ApiError, api, errorMessage } from "@/lib/api";
import { money, percent } from "@/lib/format";
import { useBrokers, useCyclesAwaitingAccount, usePropFirms, useRefreshDirectory, useServers, useSession, useSessionsAwaitingProp } from "@/lib/queries";
import { decimalInput, fractionToPct, pctIssue, pctToFraction } from "@/lib/units";
import type { Account, BrokerServer, CycleAwaitingAccount, PropFirm, SessionAwaitingProp } from "@/lib/types";

/*
 * IL PERCORSO CICLO-FASI, UNICO E GUIDATO, DALLA SCHEDA CLIENTE.
 *
 * Un solo pulsante — «Aggiungi conto» — invece di tre porte separate
 * (`/fasi/nuova`, `/cycles/:id/next-account` chiuso dietro un curl, e niente
 * per il broker che arriva dopo). Si dichiara SEMPRE la fase (Fase 1, Fase 2,
 * Funded) e SEMPRE la prop firm col suo programma.
 *
 * La casella «Contabilità / ciclo» è SPENTA per difetto: senza, la Fase opera da
 * sola — nessun ciclo, nessun periodo, nessun registro — e il ciclo si crea
 * dopo, a comando, da una Fase 1 («Crea ciclo»). Con la casella accesa la Fase 1
 * apre un ciclo nuovo, e Fase 2 e Funded continuano quello che aspetta il conto:
 * mai una combinazione che il backend rifiuterebbe.
 *
 * Il broker è la terza scelta indipendente: libero fra quelli del cliente,
 * nuovo, o nessuno. Senza broker la Fase nasce lo stesso, `AWAITING_BROKER`
 * — si aggancia più tardi dalla scheda della Fase (`AttachBrokerPanel`).
 *
 * PRIMA DI TUTTO si sceglie che tipo di conto è. Prop = il percorso qui sopra.
 * Broker = solo il conto (login, password, server, valuta, etichetta): nessuna
 * Fase, nessun ciclo; resta fra i broker liberi del cliente, pronto per una Fase.
 */

type AccountKind = "PROP" | "BROKER";

const BASES = Object.keys(LABELS.ddBasis) as DdBasis[];

const cicloAttesaLabel = (c: CycleAwaitingAccount) =>
  `${PHASE_LABEL(c.awaitingPropPhase)} · ${c.label ?? c.propFirm.name} · ${LABELS.terms.recoverAmount} ${money(c.recoverAmount, c.currency)}`;

const PHASES: CyclePhase[] = ["PHASE_1", "PHASE_2", "FUNDED"];

const sessioneAttesaLabel = (f: SessionAwaitingProp) =>
  `${PHASE_LABEL(f.awaitingPropPhase)} · ${f.preset ? `${f.preset.propFirm.name} ${f.preset.name}` : "senza programma"} · senza contabilità`;

/** Il target di fase del programma, per la fase dichiarata. */
const targetOf = (p: { phase1TargetPct: string | null; phase2TargetPct: string | null; fundedTargetPct: string | null }, phase: CyclePhase) =>
  phase === "PHASE_1" ? p.phase1TargetPct : phase === "PHASE_2" ? p.phase2TargetPct : p.fundedTargetPct;

export function NewPropAccountPanel({
  open,
  clientId,
  clientName,
  brokerAccounts,
  /** Aperto da "Registra il conto" su un ciclo preciso: il menu parte già lì. */
  defaultTargetCycleId,
  /** Come sopra, per una Fase SENZA ciclo che aspetta il conto. */
  defaultTargetSessionId,
  onClose,
}: {
  open: boolean;
  clientId: string;
  clientName: string;
  /** I conti broker del cliente: da qui si sceglie quello "libero fra i miei". */
  brokerAccounts: Account[];
  defaultTargetCycleId?: string;
  defaultTargetSessionId?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const firms = usePropFirms();
  const brokers = useBrokers();
  const awaiting = useCyclesAwaitingAccount({ clientId });
  const awaitingSessions = useSessionsAwaitingProp({ clientId });

  /** Che tipo di conto si crea. Nessuna preselezione, salvo «Registra il conto» su un'attesa: lì è per forza il prop. */
  const [kind, setKind] = useState<AccountKind | null>(defaultTargetCycleId || defaultTargetSessionId ? "PROP" : null);

  // --- Il conto prop nuovo ---
  const [mt5Login, setMt5Login] = useState("");
  const [mt5Password, setMt5Password] = useState("");
  /** Si sceglie PRIMA la prop firm, poi il server: quelli già legati a lei stanno in cima. */
  const [propFirmId, setPropFirmId] = useState("");
  const [propServerId, setPropServerId] = useState("");
  const [propCurrency, setPropCurrency] = useState("USD");
  const [propLabel, setPropLabel] = useState("");

  /*
   * CONTABILITA' / CICLO, SPENTA per difetto. Se il pannello si apre su un ciclo
   * che aspetta il conto, la contabilita' c'e' gia': si parte accesa.
   */
  const [accounting, setAccounting] = useState<boolean>(!!defaultTargetCycleId);
  // --- La fase: "NEW", "cycle:<id>" (un ciclo che aspetta) o "session:<id>" (una Fase senza ciclo che aspetta) ---
  const [target, setTarget] = useState<string>(defaultTargetCycleId ? `cycle:${defaultTargetCycleId}` : defaultTargetSessionId ? `session:${defaultTargetSessionId}` : "NEW");
  // Una scelta che l'altra modalita' non offre non resta selezionata di nascosto: si torna a «nuova».
  const targetOk = target === "NEW" || (accounting ? target.startsWith("cycle:") : target.startsWith("session:"));
  const effTarget = targetOk ? target : "NEW";
  const nuovaChallenge = effTarget === "NEW";
  const cicloScelto = effTarget.startsWith("cycle:") ? (awaiting.data?.find((c) => c.id === effTarget.slice(6)) ?? null) : null;
  const faseScelta = effTarget.startsWith("session:") ? (awaitingSessions.data?.find((f) => f.id === effTarget.slice(8)) ?? null) : null;
  // La fase che si dichiara: con la contabilita' accesa una nuova challenge e' sempre la Fase 1.
  const [phaseChoice, setPhaseChoice] = useState<CyclePhase>("PHASE_1");

  // --- Solo per una challenge nuova ---
  const [presetId, setPresetId] = useState("");
  const [accountSize, setAccountSize] = useState("");
  const [fee, setFee] = useState("");
  const [maxDdPct, setMaxDdPct] = useState("");
  const [basis, setBasis] = useState<DdBasis>("STATIC_INITIAL");
  const [cycleLabel, setCycleLabel] = useState("");
  const [dailyDdPct, setDailyDdPct] = useState("");
  const [targetPct, setTargetPct] = useState("");
  const [symbol, setSymbol] = useState("");

  // --- Il broker: libero, nuovo, o nessuno ---
  const [brokerChoice, setBrokerChoice] = useState<"NONE" | "EXISTING" | "NEW">(brokerAccounts.some((a) => !a.liveSession) ? "EXISTING" : "NONE");
  const [brokerAccountId, setBrokerAccountId] = useState(() => brokerAccounts.find((a) => !a.liveSession)?.id ?? "");
  const [newBrokerLogin, setNewBrokerLogin] = useState("");
  const [newBrokerPassword, setNewBrokerPassword] = useState("");
  /** Come per il prop: prima il broker, poi il server. Vale per il conto broker da solo e per «Il broker → Nuovo». */
  const [brokerOwnerId, setBrokerOwnerId] = useState("");
  const [newBrokerServerId, setNewBrokerServerId] = useState("");
  const [newBrokerCurrency, setNewBrokerCurrency] = useState("USD");
  const [newBrokerLabel, setNewBrokerLabel] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const propServers = (servers.data ?? []).filter((s) => s.kind === "PROP");
  const brokerServers = (servers.data ?? []).filter((s) => s.kind === "BROKER");
  const propServer = propServers.find((s) => s.id === propServerId);
  const newBrokerServer = brokerServers.find((s) => s.id === newBrokerServerId);
  const allPresets = (firms.data ?? []).flatMap((f) => f.presets.map((p) => ({ ...p, firmName: f.name })));
  const preset = allPresets.find((p) => p.id === presetId);
  // Continuando un ciclo o una Fase che aspettano il conto la prop firm è la loro: non si cambia a metà.
  const firmDelTarget = cicloScelto?.propFirm.id ?? faseScelta?.preset?.propFirm.id ?? null;
  const firmBloccata = !nuovaChallenge && firmDelTarget !== null;
  const effFirmId = firmBloccata ? firmDelTarget : propFirmId;
  const programmi = (firms.data ?? []).find((f) => f.id === effFirmId)?.presets ?? [];
  const brokerLiberi = brokerAccounts.filter((a) => !a.liveSession);

  // Con la contabilita' accesa una nuova challenge parte sempre dalla Fase 1.
  const phase: CyclePhase = accounting ? "PHASE_1" : phaseChoice;
  // Una Fase 2 esiste solo nei programmi a due step: il backend rifiuterebbe (422) e qui non si offre.
  const phase2Available = !preset || preset.phases >= 2;

  // Preset o fase scelti: si precompilano taglia, fee, DD e il target di QUELLA fase.
  useEffect(() => {
    if (!preset || !nuovaChallenge) return;
    setAccountSize(String(Number(preset.accountSize)));
    // Uno ZERO del programma non e' una fee: nel catalogo vuol dire «non dichiarata», e precompilato
    // finirebbe nel «Da recuperare» come una challenge gratis. Il campo resta vuoto e obbligatorio.
    // Vale anche cambiando programma: la fee del programma di prima non resta a fare da valore.
    setFee(Number(preset.challengeFee) > 0 ? String(Number(preset.challengeFee)) : "");
    setMaxDdPct(fractionToPct(preset.maxDdPct));
    setBasis(preset.maxDdBasis);
    setDailyDdPct(fractionToPct(preset.dailyDdPct));
    setTargetPct(fractionToPct(targetOf(preset, phase)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId, phase]);

  // Un programma senza Fase 2 non lascia selezionata una Fase 2.
  useEffect(() => {
    if (phaseChoice === "PHASE_2" && !phase2Available) setPhaseChoice("PHASE_1");
  }, [phaseChoice, phase2Available]);

  const effective = {
    accountSize: decimalInput(accountSize),
    maxDdPct: pctToFraction(maxDdPct),
    fee: decimalInput(fee),
  };

  // Simbolo: solo per una challenge nuova. Con broker scelto (libero o nuovo con
  // server noto), l'elenco è l'intersezione fra i due server — la stessa regola
  // dell'apertura. Senza broker, quello che è mappato sul server prop: il
  // backend lo verifica di nuovo quando il broker arriva.
  const brokerServerForSymbols =
    brokerChoice === "EXISTING" ? servers.data?.find((s) => s.id === brokerAccounts.find((a) => a.id === brokerAccountId)?.serverId) : brokerChoice === "NEW" ? newBrokerServer : undefined;
  const symbolOptions = !propServer
    ? []
    : brokerServerForSymbols
      ? propServer.symbols.map((s) => s.canonical).filter((c) => brokerServerForSymbols.symbols.some((b) => b.canonical === c)).sort()
      : [...propServer.symbols.map((s) => s.canonical)].sort();

  const brokerProblems = [
    !brokerOwnerId && "broker",
    !newBrokerLogin.trim() && "login MT5 del conto broker",
    !newBrokerPassword && "password del conto broker",
    !newBrokerServerId && "server del conto broker",
  ];
  const problems = (
    kind === null
      ? ["il tipo di conto"]
      : kind === "BROKER"
        ? brokerProblems
        : [
            !mt5Login.trim() && "login MT5 del conto prop",
            !mt5Password && "password del conto prop",
            !effFirmId && "prop firm",
            !propServerId && "server del conto prop",
            nuovaChallenge && !presetId && "programma",
            nuovaChallenge && !effective.accountSize && "taglia del conto",
            nuovaChallenge && !effective.maxDdPct && "max drawdown",
            nuovaChallenge && accounting && !effective.fee && "fee",
            nuovaChallenge && !symbol && "simbolo",
            // Un testo non capito nei due campi facoltativi non deve partire come «vuoto».
            nuovaChallenge && pctIssue("DD giornaliero", dailyDdPct),
            nuovaChallenge && pctIssue("Target", targetPct),
            !nuovaChallenge && !cicloScelto && !faseScelta && "quale fase continuare",
            brokerChoice === "EXISTING" && !brokerAccountId && "quale conto broker",
            ...(brokerChoice === "NEW" ? brokerProblems : []),
          ]
  ).filter(Boolean) as string[];

  const submit = async () => {
    if (problems.length) return;
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      // Un conto broker da solo: nessuna Fase, nessun ciclo. Resta libero, pronto per essere agganciato.
      if (kind === "BROKER") {
        await api.accounts.create({
          label: newBrokerLabel.trim() || `broker ${newBrokerLogin.trim()}`,
          clientId,
          role: "BROKER",
          mt5Login: newBrokerLogin.trim(),
          mt5Password: newBrokerPassword,
          serverId: newBrokerServerId,
          currency: newBrokerCurrency,
        });
        await refresh();
        onClose();
        return;
      }

      // Il conto prop nasce sempre: è il motivo per cui si è aperto questo pannello.
      const prop = await api.accounts.create({
        label: propLabel.trim() || `prop ${mt5Login.trim()}`,
        clientId,
        role: "PROP",
        mt5Login: mt5Login.trim(),
        mt5Password,
        serverId: propServerId,
        currency: propCurrency,
      });

      if (!nuovaChallenge && (cicloScelto || faseScelta)) {
        const body =
          brokerChoice === "EXISTING"
            ? { accountId: prop.id, brokerAccountId }
            : brokerChoice === "NEW"
              ? { accountId: prop.id, newBroker: { mt5Login: newBrokerLogin.trim(), mt5Password: newBrokerPassword, serverId: newBrokerServerId, label: newBrokerLabel.trim() || null, currency: newBrokerCurrency } }
              : { accountId: prop.id, noBroker: true as const };
        const r = cicloScelto ? await api.cycles.nextAccount(cicloScelto.id, body) : await api.sessions.nextAccount(faseScelta!.id, body);
        await refresh();
        navigate(`/fasi/${r.session.id}`);
        return;
      }

      let brokerId: string | undefined;
      if (brokerChoice === "EXISTING") brokerId = brokerAccountId;
      else if (brokerChoice === "NEW") {
        const broker = await api.accounts.create({
          label: newBrokerLabel.trim() || `broker ${newBrokerLogin.trim()}`,
          clientId,
          role: "BROKER",
          mt5Login: newBrokerLogin.trim(),
          mt5Password: newBrokerPassword,
          serverId: newBrokerServerId,
          currency: newBrokerCurrency,
        });
        brokerId = broker.id;
      }

      const r = await api.sessions.create({
        clientId,
        propAccountId: prop.id,
        ...(brokerId && { brokerAccountId: brokerId }),
        canonicalSymbol: symbol,
        phase,
        presetId,
        // Sempre esplicito: l'API, senza, vale `true`.
        accounting,
        // Etichetta e fee sono del CICLO: senza contabilita' il backend le rifiuterebbe.
        ...(accounting ? { cycleLabel: cycleLabel.trim() || null, challengeFee: effective.fee! } : {}),
        accountSize: effective.accountSize!,
        maxDdPct: effective.maxDdPct!,
        maxDdBasis: basis,
        dailyDdPct: pctToFraction(dailyDdPct),
        challengeTargetPct: pctToFraction(targetPct),
      });
      await refresh();
      navigate(`/fasi/${r.session.id}`);
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={kind === "PROP" ? "Nuovo conto prop" : kind === "BROKER" ? "Nuovo conto broker" : "Aggiungi conto"}
      subtitle={clientName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy || problems.length > 0} onClick={() => void submit()} title={problems.length ? `Manca: ${problems.join(", ")}` : undefined}>
            {busy ? "Creazione…" : "Crea"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <section className="space-y-3">
          <h3 className="text-sm font-medium text-ink">Che tipo di conto è?</h3>
          <div role="radiogroup" aria-label="Tipo di conto" className="grid grid-cols-2 gap-2">
            {(
              [
                ["PROP", "Conto prop"],
                ["BROKER", "Conto broker"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={kind === value}
                onClick={() => setKind(value)}
                className={`rounded border px-2 py-1.5 text-xs ${kind === value ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
              >
                {label}
              </button>
            ))}
          </div>
          {kind === null && (
            <p className="text-xs text-muted">
              Il prop è la gamba di partenza e porta con sé la fase; il broker è il conto che riceve la gamba opposta.
            </p>
          )}
        </section>

        {kind === "BROKER" && (
          <section className="space-y-3 border-t border-line pt-4">
            <h3 className="text-sm font-medium text-ink">Il conto broker</h3>
            <NewBrokerFields
              brokers={brokers.data ?? []}
              brokerId={brokerOwnerId}
              onBrokerId={setBrokerOwnerId}
              servers={brokerServers}
              login={newBrokerLogin}
              onLogin={setNewBrokerLogin}
              password={newBrokerPassword}
              onPassword={setNewBrokerPassword}
              serverId={newBrokerServerId}
              onServerId={setNewBrokerServerId}
              currency={newBrokerCurrency}
              onCurrency={setNewBrokerCurrency}
              label={newBrokerLabel}
              onLabel={setNewBrokerLabel}
            />
            <p className="rounded-card border border-line bg-surface p-3 text-xs text-muted">
              Nessuna fase e nessun ciclo: il conto entra fra i broker liberi di questo cliente, e si aggancia a una fase quando serve.
            </p>
          </section>
        )}

        {kind === "PROP" && (
        <>
        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-ink">Il conto prop</h3>
          <Field label="Login MT5">{(id) => <input id={id} value={mt5Login} onChange={(e) => setMt5Login(e.target.value)} className={inputClass} />}</Field>
          <Field label="Password MT5">{(id) => <input id={id} type="password" value={mt5Password} onChange={(e) => setMt5Password(e.target.value)} className={inputClass} />}</Field>
          <OwnerServerFields
            kind="PROP"
            owners={firms.data ?? []}
            ownerId={effFirmId}
            onOwnerId={(v) => {
              setPropFirmId(v);
              // Il programma è di UNA prop firm: cambiando firm non resta selezionato quello di un'altra.
              setPresetId("");
            }}
            ownerLocked={firmBloccata}
            servers={propServers}
            serverId={propServerId}
            onServerId={setPropServerId}
          >
            <Field label="Valuta">{(id) => <input id={id} value={propCurrency} maxLength={3} onChange={(e) => setPropCurrency(e.target.value.toUpperCase())} className={inputClass} />}</Field>
          </OwnerServerFields>
          <Field label="Etichetta" hint="Facoltativa: se vuota, prop + il login.">
            {(id) => <input id={id} value={propLabel} onChange={(e) => setPropLabel(e.target.value)} className={inputClass} />}
          </Field>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-ink">La fase</h3>

          {/*
            * LA CONTABILITA' e' una scelta di questa Fase, spenta per difetto:
            * accesa, nasce un ciclo (fee, recover, periodi, split); spenta,
            * la Fase opera da sola e il ciclo si crea dopo.
            */}
          <label className="flex items-start gap-2 rounded-card border border-line bg-surface p-3 text-sm">
            <input type="checkbox" checked={accounting} onChange={(e) => setAccounting(e.target.checked)} className="mt-0.5" />
            <span>
              <span className="font-medium text-ink">Contabilità / ciclo</span>
              <span className="mt-0.5 block text-xs text-muted">
                {accounting
                  ? "Nasce un ciclo: fee, da recuperare, periodi sul saldo del broker e split. Una Fase 1 apre un ciclo nuovo; Fase 2 e Funded continuano quello che aspetta il conto."
                  : "La fase opera da sola, senza ciclo: niente registro né recover. Il ciclo si crea dopo dal menu della fase («Crea ciclo»), solo da una Fase 1."}
              </span>
            </span>
          </label>

          <Field label="Continua">
            {(id) => (
              <select id={id} value={effTarget} onChange={(e) => setTarget(e.target.value)} className={inputClass}>
                <option value="NEW">{accounting ? "Fase 1 · nuova challenge" : "Nuova fase"}</option>
                {accounting
                  ? (awaiting.data ?? []).map((c) => (
                      <option key={c.id} value={`cycle:${c.id}`}>
                        {cicloAttesaLabel(c)}
                      </option>
                    ))
                  : (awaitingSessions.data ?? []).map((f) => (
                      <option key={f.id} value={`session:${f.id}`}>
                        {sessioneAttesaLabel(f)}
                      </option>
                    ))}
              </select>
            )}
          </Field>

          {!nuovaChallenge && cicloScelto && (
            <dl className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface p-4 text-sm">
              <Info label="Prop firm">{cicloScelto.propFirm.name}</Info>
              <Info label="Fase che aspetta">{PHASE_LABEL(cicloScelto.awaitingPropPhase)}</Info>
              <Info label={LABELS.terms.recoverAmount}>{money(cicloScelto.recoverAmount, cicloScelto.currency)}</Info>
              <Info label="Simbolo">{cicloScelto.lastPhase?.canonicalSymbol ?? "—"}</Info>
              <p className="col-span-2 text-xs text-faint">
                Simbolo, DD e target si ricopiano dall'ultima fase: continuano la stessa coppia.
              </p>
            </dl>
          )}

          {!nuovaChallenge && faseScelta && (
            <dl className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface p-4 text-sm">
              <Info label="Prop firm">{faseScelta.preset ? `${faseScelta.preset.propFirm.name} · ${faseScelta.preset.name}` : "—"}</Info>
              <Info label="Fase che aspetta">{PHASE_LABEL(faseScelta.awaitingPropPhase)}</Info>
              <Info label="Simbolo">{faseScelta.canonicalSymbol}</Info>
              <Info label="Contabilità">Nessuna</Info>
              <p className="col-span-2 text-xs text-faint">
                Programma, taglia, DD e simbolo si ricopiano dalla fase di prima. Dopo un conto bruciato riparte la stessa fase, non la Fase 1.
              </p>
            </dl>
          )}

          {nuovaChallenge && (
            <>
              <div role="radiogroup" aria-label="Fase" className="grid grid-cols-3 gap-2">
                {PHASES.map((p) => {
                  const disabled = accounting ? p !== "PHASE_1" : p === "PHASE_2" && !phase2Available;
                  return (
                    <button
                      key={p}
                      type="button"
                      role="radio"
                      aria-checked={phase === p}
                      disabled={disabled}
                      title={
                        accounting && p !== "PHASE_1"
                          ? "Con la contabilità, Fase 2 e Funded continuano un ciclo che aspetta il conto: sceglilo da «Continua»."
                          : disabled
                            ? "Questo programma ha un solo step: non c'è una Fase 2."
                            : undefined
                      }
                      onClick={() => setPhaseChoice(p)}
                      className={`rounded border px-2 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-40 ${phase === p ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
                    >
                      {PHASE_LABEL(p)}
                    </button>
                  );
                })}
              </div>
              <Field label="Programma" hint={!effFirmId ? "Scegli prima la prop firm, in alto." : undefined}>
                {(id) => (
                  <select id={id} value={presetId} disabled={!effFirmId} onChange={(e) => setPresetId(e.target.value)} className={inputClass}>
                    <option value="" disabled>
                      {!effFirmId ? "Scegli prima la prop firm" : programmi.length ? "Scegli il programma" : "Nessun programma per questa prop firm"}
                    </option>
                    {programmi.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {p.phases === 0 ? "instant" : p.phases === 1 ? "one-step" : "two-step"}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <NumField label="Taglia del conto" value={accountSize} onChange={setAccountSize} suffix={propCurrency} />
                {accounting && <NumField label="Fee pagata" value={fee} onChange={setFee} suffix={propCurrency} hint={preset && !(Number(preset.challengeFee) > 0) ? "Il programma non dichiara la fee: scrivi quella pagata." : undefined} />}
                <NumField label="Max drawdown" value={maxDdPct} onChange={setMaxDdPct} suffix="%" />
                <Field label="Base del DD">
                  {(id) => (
                    <select id={id} value={basis} onChange={(e) => setBasis(e.target.value as DdBasis)} className={inputClass}>
                      {BASES.map((b) => (
                        <option key={b} value={b}>
                          {LABELS.ddBasis[b]}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>
              {accounting && (
                <Field label="Etichetta del ciclo" hint="Facoltativa, per riconoscerlo nello storico.">
                  {(id) => <input id={id} value={cycleLabel} onChange={(e) => setCycleLabel(e.target.value)} className={inputClass} />}
                </Field>
              )}
              <div className="grid grid-cols-2 gap-3">
                <NumField label="DD giornaliero" value={dailyDdPct} onChange={setDailyDdPct} suffix="%" hint="Vuoto se non previsto." />
                <NumField label={`Target ${PHASE_LABEL(phase).toLowerCase()}`} value={targetPct} onChange={setTargetPct} suffix="%" />
              </div>
              <Field label="Simbolo" hint={!symbolOptions.length ? "Nessun simbolo mappato: mappalo dalla pagina Server, poi torna qui." : undefined}>
                {(id) => (
                  <select id={id} value={symbol} onChange={(e) => setSymbol(e.target.value)} disabled={!symbolOptions.length} className={inputClass}>
                    <option value="" disabled>
                      Scegli il simbolo
                    </option>
                    {symbolOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </>
          )}
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-ink">Il broker</h3>
          <div role="radiogroup" aria-label="Conto broker" className="grid grid-cols-3 gap-2">
            {(
              [
                ["EXISTING", "Libero fra i miei"],
                ["NEW", "Nuovo"],
                ["NONE", "Nessuno"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={brokerChoice === value}
                onClick={() => setBrokerChoice(value)}
                className={`rounded border px-2 py-1.5 text-xs ${brokerChoice === value ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
              >
                {label}
              </button>
            ))}
          </div>

          {brokerChoice === "EXISTING" && (
            <Field label="Conto broker" hint={!brokerLiberi.length ? "Nessun conto broker libero: creane uno nuovo, o lascia vuoto e aggiungilo dopo." : undefined}>
              {(id) => (
                <select id={id} value={brokerAccountId} onChange={(e) => setBrokerAccountId(e.target.value)} disabled={!brokerLiberi.length} className={inputClass}>
                  <option value="" disabled>
                    {brokerLiberi.length ? "Scegli il conto" : "Nessun conto libero"}
                  </option>
                  {brokerLiberi.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label} · {a.mt5Login}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          {brokerChoice === "NEW" && (
            <NewBrokerFields
              brokers={brokers.data ?? []}
              brokerId={brokerOwnerId}
              onBrokerId={setBrokerOwnerId}
              servers={brokerServers}
              login={newBrokerLogin}
              onLogin={setNewBrokerLogin}
              password={newBrokerPassword}
              onPassword={setNewBrokerPassword}
              serverId={newBrokerServerId}
              onServerId={setNewBrokerServerId}
              currency={newBrokerCurrency}
              onCurrency={setNewBrokerCurrency}
              label={newBrokerLabel}
              onLabel={setNewBrokerLabel}
            />
          )}

          {brokerChoice === "NONE" && (
            <p className="rounded-card border border-line bg-surface p-3 text-xs text-muted">
              La fase nasce lo stesso, in attesa del conto broker: non si apre niente finché non arriva. Si aggancia più
              tardi dalla scheda della fase.
            </p>
          )}
        </section>
        </>
        )}

        <ErrorNote>{error}</ErrorNote>
        {errorCode === "BALANCE_NOT_READ" && (
          <p className="text-xs text-warn">
            Il saldo del conto broker non si è letto: il conto prop è comunque stato creato — riprova, o registralo di
            nuovo scegliendolo fra i liberi.
          </p>
        )}
      </div>
    </SidePanel>
  );
}

/**
 * I campi di un conto broker nuovo: gli stessi nel percorso prop («Il broker →
 * Nuovo») e nel conto broker da solo. Si sceglie PRIMA il broker e poi il server,
 * con quelli già legati a quel broker in cima.
 */
function NewBrokerFields({
  brokers,
  brokerId,
  onBrokerId,
  servers,
  login,
  onLogin,
  password,
  onPassword,
  serverId,
  onServerId,
  currency,
  onCurrency,
  label,
  onLabel,
}: {
  brokers: PropFirm[];
  brokerId: string;
  onBrokerId: (v: string) => void;
  servers: BrokerServer[];
  login: string;
  onLogin: (v: string) => void;
  password: string;
  onPassword: (v: string) => void;
  serverId: string;
  onServerId: (v: string) => void;
  currency: string;
  onCurrency: (v: string) => void;
  label: string;
  onLabel: (v: string) => void;
}) {
  return (
    <>
      <Field label="Login MT5">{(id) => <input id={id} value={login} onChange={(e) => onLogin(e.target.value)} className={inputClass} />}</Field>
      <Field label="Password MT5">{(id) => <input id={id} type="password" value={password} onChange={(e) => onPassword(e.target.value)} className={inputClass} />}</Field>
      <OwnerServerFields kind="BROKER" owners={brokers} ownerId={brokerId} onOwnerId={onBrokerId} servers={servers} serverId={serverId} onServerId={onServerId}>
        <Field label="Valuta">{(id) => <input id={id} value={currency} maxLength={3} onChange={(e) => onCurrency(e.target.value.toUpperCase())} className={inputClass} />}</Field>
      </OwnerServerFields>
      <Field label="Etichetta" hint="Facoltativa: se vuota, broker + il login.">
        {(id) => <input id={id} value={label} onChange={(e) => onLabel(e.target.value)} className={inputClass} />}
      </Field>
    </>
  );
}

const NUOVO_SERVER = "__nuovo__";

/**
 * PROP FIRM (o BROKER) E POI SERVER, in quest'ordine.
 *
 * Il server dice chi possiede il conto, e la lista dei server è lunga: scelto
 * prima il possessore, quelli **già legati a lui stanno in cima**, in un gruppo
 * col suo nome, e gli altri sotto. Finché il possessore non c'è il server non si
 * sceglie — l'ordine è la struttura, non un consiglio.
 *
 * «Server nuovo…» crea il server **già legato** al possessore scelto
 * (`POST /servers` con `propFirmId`), così la volta dopo è fra i suoi. Il nome
 * deve essere quello esatto del terminale MT5: il backend lo verifica col
 * gateway e, se non lo trova, lo rifiuta e dice perché. I simboli di un server
 * nuovo si mappano da soli alla prima connessione.
 */
function OwnerServerFields({
  kind,
  owners,
  ownerId,
  onOwnerId,
  ownerLocked = false,
  servers,
  serverId,
  onServerId,
  children,
}: {
  kind: "PROP" | "BROKER";
  owners: PropFirm[];
  ownerId: string;
  onOwnerId: (v: string) => void;
  /** Continuando una Fase che aspetta il conto la prop firm è la sua. */
  ownerLocked?: boolean;
  servers: BrokerServer[];
  serverId: string;
  onServerId: (v: string) => void;
  /** Accanto al server: la valuta. */
  children?: ReactNode;
}) {
  const refresh = useRefreshDirectory();
  const [nuovo, setNuovo] = useState(false);
  const [nome, setNome] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isProp = kind === "PROP";
  const ownerLabel = isProp ? "Prop firm" : "Broker";
  const owner = owners.find((o) => o.id === ownerId) ?? null;
  const legati = servers.filter((s) => ownerId !== "" && s.propFirmId === ownerId);
  const altri = servers.filter((s) => !(ownerId !== "" && s.propFirmId === ownerId));
  const nomeServer = (s: BrokerServer) => s.displayName ?? s.name;

  const crea = async () => {
    if (!nome.trim() || !ownerId) return;
    setBusy(true);
    setError(null);
    try {
      const creato = await api.servers.create({ name: nome.trim(), kind, propFirmId: ownerId });
      await refresh();
      onServerId(creato.id);
      setNuovo(false);
      setNome("");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Field label={ownerLabel}>
        {(id) => (
          <select
            id={id}
            value={ownerId}
            disabled={ownerLocked}
            onChange={(e) => {
              onOwnerId(e.target.value);
              setNuovo(false);
            }}
            className={inputClass}
          >
            <option value="" disabled>
              {owners.length ? `Scegli ${isProp ? "la prop firm" : "il broker"}` : isProp ? "Nessuna prop firm nel catalogo" : "Nessun broker nel catalogo"}
            </option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Server">
          {(id) => (
            <select
              id={id}
              value={nuovo ? NUOVO_SERVER : serverId}
              disabled={!ownerId}
              onChange={(e) => {
                if (e.target.value === NUOVO_SERVER) setNuovo(true);
                else {
                  setNuovo(false);
                  onServerId(e.target.value);
                }
              }}
              className={inputClass}
            >
              <option value="" disabled>
                {!ownerId ? `Scegli prima ${isProp ? "la prop firm" : "il broker"}` : servers.length ? "Scegli il server" : `Nessun server ${isProp ? "prop" : "broker"}`}
              </option>
              {legati.length > 0 && (
                <optgroup label={`Di ${owner?.name ?? ownerLabel}`}>
                  {legati.map((s) => (
                    <option key={s.id} value={s.id}>
                      {nomeServer(s)}
                    </option>
                  ))}
                </optgroup>
              )}
              {altri.length > 0 && (
                <optgroup label={legati.length > 0 ? "Altri server" : "Server"}>
                  {altri.map((s) => (
                    <option key={s.id} value={s.id}>
                      {nomeServer(s)}
                    </option>
                  ))}
                </optgroup>
              )}
              {ownerId && <option value={NUOVO_SERVER}>+ Server nuovo…</option>}
            </select>
          )}
        </Field>
        {children}
      </div>
      {nuovo && ownerId && (
        <div className="space-y-2 rounded-card border border-line bg-surface p-3">
          <Field label="Nome del server" hint={`Identico a quello del terminale MT5. Nasce legato a ${owner?.name ?? ownerLabel}.`}>
            {(id) => <input id={id} value={nome} onChange={(e) => setNome(e.target.value)} className={inputClass} />}
          </Field>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="primary" disabled={busy || !nome.trim()} onClick={() => void crea()}>
              {busy ? "Verifico sul gateway…" : "Crea server"}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setNuovo(false)}>
              Annulla
            </Button>
          </div>
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
    </>
  );
}

function NumField({ label, value, onChange, suffix, hint }: { label: string; value: string; onChange: (v: string) => void; suffix?: string; hint?: string }) {
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="relative">
          <input id={id} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} ${suffix ? "pr-10" : ""}`} />
          {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
        </div>
      )}
    </Field>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Il broker arriva dopo
// ---------------------------------------------------------------------------

/**
 * AGGANCIA IL BROKER a una Fase `AWAITING_BROKER`: libero fra i conti del
 * cliente, o nuovo. Un gesto che succede una volta sola — da qui in poi la
 * Fase si comporta come una nata con broker fin dall'inizio.
 */
export function AttachBrokerPanel({
  open,
  sessionId,
  clientId,
  clientName,
  brokerAccounts,
  onClose,
}: {
  open: boolean;
  sessionId: string;
  clientId: string;
  clientName: string;
  brokerAccounts: Account[];
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const brokerServers = (servers.data ?? []).filter((s) => s.kind === "BROKER");
  const brokerLiberi = brokerAccounts.filter((a) => !a.liveSession);

  const [choice, setChoice] = useState<"EXISTING" | "NEW">(brokerLiberi.length ? "EXISTING" : "NEW");
  const [brokerAccountId, setBrokerAccountId] = useState(brokerLiberi[0]?.id ?? "");
  const [mt5Login, setMt5Login] = useState("");
  const [mt5Password, setMt5Password] = useState("");
  const [serverId, setServerId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [label, setLabel] = useState("");
  const [baseAmount, setBaseAmount] = useState("");
  const [baseNote, setBaseNote] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const problems = [
    choice === "EXISTING" && !brokerAccountId && "quale conto broker",
    choice === "NEW" && !mt5Login.trim() && "login MT5",
    choice === "NEW" && !mt5Password && "password MT5",
    choice === "NEW" && !serverId && "server",
    choice === "NEW" && currency.trim().length !== 3 && "valuta (3 lettere, per esempio USD)",
  ].filter(Boolean) as string[];

  const submit = async () => {
    if (problems.length) return;
    const base = baseAmount.trim() ? decimalInput(baseAmount) : null;
    if (baseAmount.trim() && base === null) return setError("La base deve essere un numero, per esempio 10250.40");
    if (base !== null && baseNote.trim().length < 3) return setError("Una base indicata a mano richiede il motivo");
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      await api.sessions.attachBroker(sessionId, {
        ...(choice === "EXISTING" ? { accountId: brokerAccountId } : { mt5Login: mt5Login.trim(), mt5Password, serverId, label: label.trim() || null, currency: currency.trim().toUpperCase() }),
        ...(base !== null ? { baseAmount, baseNote: baseNote.trim() } : {}),
      });
      await refresh();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Aggiungi conto broker"
      subtitle={clientName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy || problems.length > 0} onClick={() => void submit()} title={problems.length ? `Manca: ${problems.join(", ")}` : undefined}>
            {busy ? "Aggancio…" : "Aggancia"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div role="radiogroup" aria-label="Conto broker" className="grid grid-cols-2 gap-2">
          {(
            [
              ["EXISTING", "Libero fra i miei"],
              ["NEW", "Nuovo"],
            ] as const
          ).map(([value, l]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={choice === value}
              onClick={() => setChoice(value)}
              className={`rounded border px-2 py-1.5 text-xs ${choice === value ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
            >
              {l}
            </button>
          ))}
        </div>

        {choice === "EXISTING" ? (
          <Field label="Conto broker" hint={!brokerLiberi.length ? "Nessun conto libero: creane uno nuovo." : undefined}>
            {(id) => (
              <select id={id} value={brokerAccountId} onChange={(e) => setBrokerAccountId(e.target.value)} disabled={!brokerLiberi.length} className={inputClass}>
                <option value="" disabled>
                  {brokerLiberi.length ? "Scegli il conto" : "Nessun conto libero"}
                </option>
                {brokerLiberi.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} · {a.mt5Login}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <>
            <Field label="Login MT5">{(id) => <input id={id} value={mt5Login} onChange={(e) => setMt5Login(e.target.value)} className={inputClass} />}</Field>
            <Field label="Password MT5">{(id) => <input id={id} type="password" value={mt5Password} onChange={(e) => setMt5Password(e.target.value)} className={inputClass} />}</Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Server">
                {(id) => (
                  <select id={id} value={serverId} onChange={(e) => setServerId(e.target.value)} className={inputClass}>
                    <option value="" disabled>
                      {brokerServers.length ? "Scegli il server" : "Nessun server broker"}
                    </option>
                    {brokerServers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.displayName ?? s.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="Valuta">{(id) => <input id={id} value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={inputClass} />}</Field>
            </div>
            <Field label="Etichetta" hint="Facoltativa: se vuota, broker + il login.">
              {(id) => <input id={id} value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />}
            </Field>
          </>
        )}

        <ErrorNote>{error}</ErrorNote>
        {errorCode === "BALANCE_NOT_READ" && (
          <div className="space-y-3 rounded-card border border-warn/40 bg-warn/5 p-3">
            <p className="text-xs text-ink">Il saldo non si è letto: dichiara la base del primo periodo, col motivo.</p>
            <Field label={`Base del primo periodo (${currency})`}>
              {(id) => <input id={id} inputMode="decimal" value={baseAmount} onChange={(e) => setBaseAmount(e.target.value)} className={inputClass} />}
            </Field>
            <Field label="Motivo della base">{(id) => <input id={id} value={baseNote} onChange={(e) => setBaseNote(e.target.value)} className={inputClass} />}</Field>
          </div>
        )}
      </div>
    </SidePanel>
  );
}

// ---------------------------------------------------------------------------
// «Crea ciclo»: la contabilità che nasce dopo
// ---------------------------------------------------------------------------

/** Perché «Crea ciclo» non c'è su una Fase 2 o Funded: lo stesso testo che dà il backend. */
export const CREATE_CYCLE_ONLY_PHASE_1 =
  "Si può creare il ciclo solo da una Fase 1: per Fase 2 e Funded mancano fee e periodi dei tratti precedenti, e non si possono ricostruire.";

/**
 * «Crea ciclo» su una Fase che opera senza. Il ciclo nasce adesso e i suoi
 * numeri partono da adesso; con un broker agganciato il primo periodo parte dal
 * saldo letto in questo momento, e se non si legge non nasce niente.
 *
 * `variant="menu"` per i menu «…» delle liste, `"button"` per l'intestazione
 * della Fase. Su una Fase 2 o Funded la voce c'è ma è disabilitata, col motivo.
 */
export function CreateCycleAction({
  session,
  variant = "menu",
}: {
  session: { id: string; phase: CyclePhase; cycle: unknown };
  variant?: "menu" | "button";
}) {
  const [open, setOpen] = useState(false);
  if (session.cycle) return null;
  const disabled = session.phase !== "PHASE_1";
  return (
    <>
      {variant === "menu" ? (
        <button
          type="button"
          role="menuitem"
          disabled={disabled}
          title={disabled ? CREATE_CYCLE_ONLY_PHASE_1 : undefined}
          onClick={() => setOpen(true)}
          className="block w-full rounded px-2 py-1.5 text-left text-sm text-ink hover:bg-raised disabled:cursor-not-allowed disabled:text-faint disabled:hover:bg-transparent"
        >
          Crea ciclo
        </button>
      ) : (
        <Button disabled={disabled} title={disabled ? CREATE_CYCLE_ONLY_PHASE_1 : undefined} onClick={() => setOpen(true)}>
          Crea ciclo
        </Button>
      )}
      {open && <CreateCyclePanel sessionId={session.id} onClose={() => setOpen(false)} />}
    </>
  );
}

function CreateCyclePanel({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const refresh = useRefreshDirectory();
  const [label, setLabel] = useState("");
  const [fee, setFee] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  /*
   * LA FEE DEL PROGRAMMA VALE COME PREDEFINITO SOLO SE E' MAGGIORE DI ZERO.
   * Nel catalogo zero vuol dire «non dichiarata»: prenderlo farebbe partire il
   * ciclo come una challenge gratis, e il «Da recuperare» sarebbe sbagliato.
   * Senza un programma noto (o finché la Fase si carica) la fee e' obbligatoria.
   * Uno zero scritto a mano resta valido: e' una dichiarazione.
   */
  const session = useSession(sessionId);
  const programFee = Number(session.data?.preset?.challengeFee ?? 0);
  const feeRequired = !(programFee > 0);

  const submit = async () => {
    const feeValue = fee.trim() ? decimalInput(fee) : null;
    if (fee.trim() && feeValue === null) return setError("La fee deve essere un numero");
    if (feeRequired && feeValue === null) return setError("Scrivi la fee pagata: il programma non ne dichiara una");
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      await api.sessions.createCycle(sessionId, {
        ...(label.trim() ? { cycleLabel: label.trim() } : {}),
        ...(feeValue !== null ? { challengeFee: feeValue } : {}),
      });
      await refresh();
      onClose();
      navigate(`/fasi/${sessionId}`);
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Crea ciclo"
      subtitle="La contabilità parte adesso"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy || (feeRequired && !fee.trim())} onClick={() => void submit()}>
            {busy ? "Creazione…" : "Crea ciclo"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="rounded-card border border-line bg-surface p-3 text-sm text-muted">
          Il ciclo nasce adesso e i suoi numeri partono da adesso: il costo recuperato da operazioni precedenti non gli appartiene e non
          viene contato. La fee entra nel registro; il primo periodo parte dal saldo del broker <span className="text-ink">letto in questo momento</span>.
          Se il saldo non si legge, non nasce niente.
        </p>
        <Field
          label="Fee pagata"
          hint={feeRequired ? "Obbligatoria: il programma non dichiara la fee, e uno zero preso da lì sarebbe una challenge gratis." : `Vuoto = quella del programma (${money(programFee, session.data?.propAccount.currency ?? "USD")}).`}
        >
          {(id) => <input id={id} inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} className={inputClass} />}
        </Field>
        <Field label="Etichetta del ciclo" hint="Facoltativa, per riconoscerlo nello storico.">
          {(id) => <input id={id} value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />}
        </Field>
        <ErrorNote>{error}</ErrorNote>
        {errorCode === "PAIRS_OPEN" && <p className="text-xs text-warn">Chiudi le operazioni aperte, poi riprova.</p>}
        {errorCode === "BASE_UNREAD" && <p className="text-xs text-warn">Il conto broker non ha risposto: riprova quando è raggiungibile.</p>}
      </div>
    </SidePanel>
  );
}
